import type { ReactNode } from "react";
import { cx } from "./primitives";
import { CROWN_GOLD, CrownMark, drawCrown } from "./Crown";
import { MASK } from "../../lib/replayMask";

/**
 * GoonLords Gold's identity mark — a crown on a SOLID gold badge.
 *
 * This was a "GOLD" text chip, and the reason was real: the crown is spoken for three
 * times over — the brand mark, the fap action, and the Editor's-Choice marker in
 * ClipStats — and a fourth meaning risks re-opening what "twelve crowns and none of them
 * was the logo" closed. The collision is not hypothetical. ClipStats renders a bare gold
 * crown for the FAP COUNT and, twenty pixels below it, the uploader's tier; so does
 * CollectionDetail (fap toggle, and a GoldName in the header).
 *
 * What keeps them apart now is GROUND, not shape. Everywhere else the crown is a gold
 * mark on the page's own dark ground. The tier — and only the tier — inverts that: a WHITE
 * crown on a filled gold pill. One is a symbol sitting on the page, the other is a badge
 * stuck to it, and at a glance those read as different kinds of thing even at 9px.
 *
 * It keeps an accessible NAME, which the word used to give for free: `title` reaches
 * CrownMark's `alt`, so the badge still announces itself as "GoonLords Gold".
 *
 * Note the profile ALSO shows an EARNED ladder whose top rank is literally "Goon Lord"
 * (ADDICTION_TIERS) as an OUTLINED gold pill on the share card. That is a rank you goon
 * your way into; this is a tier you pay for. Both may appear on one profile — filled
 * badge versus outlined pill is what keeps them legible side by side.
 */
export function GoldChip({ className }: { className?: string }) {
  return (
    <span
      className={cx(
        "inline-flex shrink-0 items-center rounded-full bg-[var(--color-accent)] px-1.5 py-[3px]",
        className,
      )}
      title="GoonLords Premium"
    >
      <CrownMark size={11} tone="white" title="GoonLords Premium" />
    </span>
  );
}

/** A username with its tier: the one spelling of "name + GOLD chip" every surface uses. */
export function GoldName({
  name,
  gold,
  className,
}: {
  name: ReactNode;
  gold: boolean | undefined;
  className?: string;
}) {
  return (
    <span className={cx("inline-flex min-w-0 items-center gap-1.5", className)}>
      {/* The name is somebody's handle: masked in session replay (lib/replayMask). The
          GOLD chip beside it is ours and stays readable. */}
      <span className="truncate" {...MASK}>{name}</span>
      {gold ? <GoldChip /> : null}
    </span>
  );
}

/**
 * The badge's canvas twin, for the share-card renderers (a canvas can't mount a
 * component). Sized by `h`; returns the width drawn so the caller can lay out around it.
 *
 * SOLID fill with a WHITE crown, matching GoldChip — and deliberately the inverse of the
 * outlined earned-tier pill (cardKit's drawPill) that sits beside it on the profile card.
 * Same reasoning as the DOM: ground, not shape, is what separates the paid tier from the
 * three other things a crown already means.
 *
 * The crown comes from `drawCrown`, never a copied path — tools/checks/crown-mark.mts
 * enforces that there is exactly one crown geometry in the codebase.
 */
export function drawGoldPill(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  h: number,
): number {
  const markH = h * 0.5;
  const w = h * 1.5;
  const r = h / 2;
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
  ctx.fillStyle = CROWN_GOLD;
  ctx.fill();
  // Centred by the mark's own measured width, not by an assumed aspect: drawCrown reports
  // what it drew, so the badge stays centred if the geometry is ever revised.
  const markW = (markH * 24) / 20;
  drawCrown(ctx, x + (w - markW) / 2, y + (h - markH) / 2, markH, "#ffffff");
  ctx.restore();
  return w;
}
